import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MessageSquare, Send, Users, Clock, CheckCircle, XCircle } from 'lucide-react';
import { smsApi } from '@/lib/api';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { toast } from '@/components/ui/use-toast';

interface SMSLog {
  id?: number;
  recipient_type?: string;
  phone?: string;
  recipient_group?: string;
  recipient_count?: number;
  message?: string;
  status: string;
  created_at?: string;
  sent_at?: string;
  count?: number;
}

interface SMSResponse {
  data: SMSLog[];
}

interface SMSConfigStatus {
  enabled: boolean;
  base_url?: string;
  identifier?: string;
  sender_name?: string;
  message?: string;
}

const AdminSMS = () => {
  const [message, setMessage] = useState('');
  const [recipient, setRecipient] = useState('all');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [smsStats, setSmsStats] = useState<any>(null);
  const [smsLogs, setSmsLogs] = useState<SMSLog[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const fetchSMSData = async () => {
      try {
        setIsLoadingData(true);
        const [stats, logs, configStatus] = await Promise.all([
          smsApi.getStats(),
          smsApi.getLogs(),
          smsApi.getConfigStatus()
        ]);

        // Debug: Log the current SMS configuration status
        console.log('📱 SMS Configuration Status:', configStatus);

        // Only show warning if SMS service is definitely not configured
        const config = configStatus as SMSConfigStatus;
        if (config && config.enabled === false && config.message) {
          toast({
            title: "Warning",
            description: "SMS service may not be fully configured. Please verify settings if you encounter issues.",
            variant: "destructive",
          });
        }

        setSmsStats(stats);
        setSmsLogs(Array.isArray(logs) ? logs : (logs as SMSResponse).data || []);
      } catch (error: any) {
        console.error('Error fetching SMS data:', error);
        toast({
          title: "Error",
          description: "Failed to load SMS data",
          variant: "destructive",
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchSMSData();
  }, []);

  const handleSendSMS = async () => {
    if (!message.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      if (recipient === 'single') {
        if (!phoneNumber.trim()) {
          toast({
            title: "Error",
            description: "Please enter a phone number",
            variant: "destructive",
          });
          return;
        }
        await smsApi.sendSingle(phoneNumber, message);
      } else if (recipient === 'all') {
        // Send to all members
        await smsApi.sendFiltered({}, message);
      } else {
        // Send to filtered group
        await smsApi.sendFiltered({ membership_type: recipient }, message);
      }

      toast({
        title: "Success",
        description: "SMS sent successfully",
      });

      // Clear form
      setMessage('');
      setPhoneNumber('');
      
      // Refresh data
      const [stats, logs] = await Promise.all([
        smsApi.getStats(),
        smsApi.getLogs()
      ]);
      setSmsStats(stats);
      setSmsLogs(Array.isArray(logs) ? logs : (logs as SMSResponse).data || []);
    } catch (error: any) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send SMS",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Delivered</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'failed':
      case 'failure':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString() + ' ' + 
             new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString;
    }
  };

  // Default stats if API data is not available
  const defaultStats = [
    {
      title: 'SMS Sent Today',
      value: smsStats?.sent_today || '0',
      icon: Send,
      color: 'text-blue-600'
    },
    {
      title: 'Pending SMS',
      value: smsStats?.pending || '0',
      icon: Clock,
      color: 'text-yellow-600'
    },
    {
      title: 'Delivered',
      value: smsStats?.delivered || '0',
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Failed',
      value: smsStats?.failed || '0',
      icon: XCircle,
      color: 'text-red-600'
    },
  ];

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SMS Management</h1>
          <p className="text-gray-600 mt-2">
            Send messages to members and track SMS delivery status
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {defaultStats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Send SMS */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Send SMS</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipients
                  </label>
                  <Select value={recipient} onValueChange={setRecipient}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      <SelectItem value="Active OG">Active OG Members</SelectItem>
                      <SelectItem value="Social">Social Members</SelectItem>
                      <SelectItem value="Youth">Youth Members</SelectItem>
                      <SelectItem value="single">Single Number</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {recipient === 'single' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="+255621728109"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message ({message.length}/160)
                  </label>
                  <Textarea
                    placeholder="Type your message here..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    maxLength={160}
                  />
                </div>

                <Button 
                  onClick={handleSendSMS}
                  disabled={isLoading}
                  className="w-full bg-basketball-gradient hover:opacity-90"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send SMS
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* SMS Logs */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Recent SMS</CardTitle>
              </CardHeader>
              <CardContent>
                {smsLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No SMS history found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {smsLogs.slice(0, 10).map((sms, index) => (
                        <TableRow key={sms.id || index}>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {sms.recipient_type === 'single' 
                                  ? sms.phone || 'Single Number'
                                  : sms.recipient_group || 'All Members'
                                }
                              </p>
                              {sms.recipient_count && (
                                <p className="text-sm text-gray-500">
                                  {sms.recipient_count} recipients
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm truncate max-w-xs">
                              {sms.message || 'No message content'}
                            </p>
                          </TableCell>
                          <TableCell>{getStatusBadge(sms.status)}</TableCell>
                          <TableCell className="text-sm">
                            {formatDate(sms.created_at || sms.sent_at)}
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {sms.recipient_count || sms.count || 1}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSMS;
